import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input } from '@angular/core';

import { Store } from '@ngrx/store';

import { formatDate } from '@lib/commons/util-functions/format-date.function';

import { DocumentIconComponent } from '@lib/medical-documents/components/document-icon/document-icon.component';
import { setCurrentDocumentDetails } from '@lib/medical-documents/core/store/medical-documents.actions';
import { selectCurrentDocument } from '@lib/medical-documents/core/store/medical-documents.selectors';

import { MedicalDocumentItem } from '../../../../core/models/document.interface';

@Component({
  selector: 'md-list-item',
  standalone: true,
  imports: [NgClass, DocumentIconComponent],
  template: `
    <div class="flex w-full cursor-pointer gap-3 rounded-xl border-2 p-2" [ngClass]="documentColor()" (click)="selectDocument()">
      <div class="flex w-full items-center justify-between">
        <div class="flex items-center gap-3">
          <md-document-icon [documentType]="document().type"></md-document-icon>
          <div>
            <div class="text-sm text-neutral-300">{{ formatDate(document().createdAt) }}</div>
            <div class="max-w-[300px] overflow-hidden text-ellipsis whitespace-nowrap text-sm font-semibold text-neutral-500">
              {{ document().documentName }}
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListItemComponent {
  protected readonly destroyRef = inject(DestroyRef);

  protected readonly store = inject(Store);

  protected readonly formatDate = formatDate;

  readonly document = input.required<MedicalDocumentItem>();

  protected readonly currentDocument = this.store.selectSignal<MedicalDocumentItem>(selectCurrentDocument);

  protected readonly selected = computed<boolean>(() => {
    if (this.currentDocument() === null) return false;
    return this.document().documentId === this.currentDocument().documentId && this.document().medicalRecordId === this.currentDocument().medicalRecordId;
  });

  protected readonly documentColor = computed<string>(() => {
    if (this.selected()) return 'bg-primary-100 border-primary-500';
    else return 'bg-neutral-100 border-neutral-100 hover:bg-primary-100 hover:border-primary-100';
  });

  protected selectDocument() {
    if (!this.selected()) this.store.dispatch(setCurrentDocumentDetails({ currentDocument: this.document() }));
  }
}
